import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogButton,
} from "@myplant-io/mui";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import UploadIcon from "@mui/icons-material/Upload";
import { defineMessages, useIntl } from "react-intl";
import { yup } from "@myplant-io/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Controller,
  SubmitHandler,
  useForm,
  useFieldArray,
} from "react-hook-form";
import {
  Checkbox,
  DialogActions,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { commonMessages } from "../../commonMessages";
import { defaultTemperature, TemperatureSlider } from "./TemperatureSlider";
import {
  defaultReasoningEffort,
  ReasoningEffortSelector,
} from "./ReasoningEffortSlider";
import {
  DocumentTypesAutocomplete,
  DocumentTypesHeading,
} from "./DocumentTypeSelector";
import { Mode, DocumentType, RagSource, EnabledFunction } from "../../types";
import { Fragment, useEffect } from "react";
import { useDocumentTypes } from "../../hooks/useDocumentType";
import {
  FilesHeading,
  FilesPreview,
  FileUploadButton,
  FileWithPreview,
  FilesDropzone,
  useFileUploadProps,
} from "./FilesSelector";
import { RagSourceInput, RagSourcesHeading } from "./RagSourceSelector";
import { FunctionsAutocomplete } from "./FunctionsSelector";
import { useFunctions } from "../../hooks/useFunctions";
import { useSnackbar } from "@myplant-io/snackbar";

const FORM_ID = "mode-edit-dialog";

export const messages = defineMessages({
  title: {
    id: "modeUpdateDialog.title",
    defaultMessage: "Agent settings",
  },
  enableThinking: {
    id: "modeUpdateDialog.enableThinking",
    defaultMessage: "Enable Thinking",
  },
  noInnioContext: {
    id: "modeUpdateDialog.noInnioContext",
    defaultMessage: "No Company Knowledge",
  },
  systemPromptAddition: {
    id: "modeUpdateDialog.systemPromptAddition",
    defaultMessage: "Instructions",
  },
  ragSources: {
    id: "modeUpdateDialog.ragSources",
    defaultMessage: "Knowledge Bases",
  },
  ragSourcesEnforceAccess: {
    id: "modeUpdateDialog.ragSourcesEnforceAccess",
    defaultMessage: "Enforce access",
  },
  ragSourcesSubfolder: {
    id: "modeUpdateDialog.ragSourcesSubfolder",
    defaultMessage: "Include subfolders",
  },
  ragSourcesIngestionNote: {
    id: "modeUpdateDialog.ragSourcesIngestionNote",
    defaultMessage:
      "Please wait while your documents are being ingested. The time required depends on the number of uploaded files.",
  },
  promptExamples: {
    id: "modeUpdateDialog.promptExamples",
    defaultMessage: "Prompt Examples",
  },
  promptExampleTooltip: {
    id: "modeUpdateDialog.promptExampleTooltip",
    defaultMessage:
      "Add examples of prompts that can be used in this agent. The examples will be shown to the user when selecting this agent.",
  },
  error: {
    id: "modeUpdateDialog.error",
    defaultMessage: "An Error occurred while saving the agent.",
  },
});

type ModeData = {
  _id?: string;
  name: string;
  temperature: number;
  reasoningEffort: string;
  enableThinking: boolean;
  systemPromptAddition: string;
  documentTypes: string[];
  enabledFunctions: string[];
  noInnioContext: boolean;
  attachments: FileWithPreview[];
  ragSources: RagSource[];
  promptExamples: string[];
};

type Props = {
  mode?: Mode;
  open: boolean;
  readOnly?: boolean;
  onClose?: () => void;
  onSubmit?: (mode: ModeData & { attachments: File[] }) => Promise<void>;
  onDelete?: (id: string) => void;
  onPublish?: (mode: Mode) => void;
};

export function ModeUpdateDialog({
  mode,
  open,
  readOnly,
  onClose,
  onSubmit,
  onDelete,
  onPublish,
}: Props) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { previewProps, buttonProps, dropzoneProps } = useFileUploadProps();
  const { showErrorMessage } = useSnackbar();
  const { formatMessage: t } = useIntl();
  const schema = yup
    .object({
      _id: yup.string(),
      name: yup.string().required(),
      temperature: yup.number().required(),
      reasoningEffort: yup.string().required(),
      enableThinking: yup.boolean().required(),
      systemPromptAddition: yup.string().default("").notRequired(),
      documentTypes: yup.array().of(yup.string().required()).required(),
      enabledFunctions: yup.array().of(yup.string().required()).required(),
      noInnioContext: yup.boolean().required(),
      attachments: yup.array().required(),
      ragSources: yup.array().required(),
      promptExamples: yup.array().of(yup.string().required()).default([]),
    })
    .required();

  const {
    handleSubmit,
    watch,
    setValue,
    control,
    reset,
    formState: { isSubmitting, isSubmitSuccessful },
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      temperature: defaultTemperature,
      reasoningEffort: defaultReasoningEffort,
      systemPromptAddition: "",
      documentTypes: [],
      enabledFunctions: [],
      noInnioContext: false,
      attachments: [],
      ragSources: [],
      promptExamples: [],
    },
  });

  const ragSourcesFieldArray = useFieldArray({
    control,
    name: "ragSources",
  });

  const promptExamplesFieldArray = useFieldArray({
    control,
    name: "promptExamples",
  });

  const { data: allDocumentTypes } = useDocumentTypes();
  const { data: allFunctions } = useFunctions();

  const setFiles = previewProps.setFiles;
  useEffect(() => {
    const files =
      mode?.attachments?.map((file) => {
        const f: FileWithPreview = new File([""], file.file_name, {});
        f.id = file._id;
        return f;
      }) ?? [];

    setFiles(files);
    reset({
      _id: mode?._id,
      name: mode?.name ?? "",
      temperature: mode?.temperature ?? defaultTemperature,
      reasoningEffort: mode?.reasoning_effort ?? defaultReasoningEffort,
      enableThinking:
        mode?.reasoning_effort !== undefined &&
        mode?.reasoning_effort !== "none",
      systemPromptAddition: mode?.system_prompt_addition ?? "",
      documentTypes: mode?.document_types ?? [],
      enabledFunctions: mode?.enabled_functions ?? [],
      noInnioContext: mode?.no_innio_context ?? false,
      attachments: files,
      ragSources: mode?.rag_sources ?? [],
      promptExamples: mode?.prompt_examples ?? [],
    });
  }, [mode, setFiles, reset]);

  useEffect(() => {
    setValue("attachments", previewProps.files);
  }, [previewProps.files, setValue]);

  const temperature = watch("temperature");
  const reasoningEffort = watch("reasoningEffort");
  const enableThinking = watch("enableThinking");
  const documentTypes = watch("documentTypes");
  const enabledFunctions = watch("enabledFunctions");
  const noInnioContext = watch("noInnioContext");
  const attachments = watch("attachments");
  const ragSources = watch("ragSources");

  const documents = allDocumentTypes?.map((doc) => ({
    ...doc,
    selected: documentTypes?.includes(doc.id),
  })) as DocumentType[];
  const functions = allFunctions
    ?.filter(({ requiresInnioContext }) =>
      noInnioContext ? requiresInnioContext === false : true
    )
    .map((doc) => ({
      ...doc,
      selected: enabledFunctions?.includes(doc.id),
    })) as EnabledFunction[];
  const _onSubmit: SubmitHandler<ModeData> = async (data) => {
    await onSubmit?.({
      _id: data._id,
      name: data.name ?? "",
      temperature: data.temperature,
      reasoningEffort: data.enableThinking ? data.reasoningEffort : "none",
      enableThinking: data.enableThinking,
      systemPromptAddition: data.systemPromptAddition ?? "",
      documentTypes: data.documentTypes,
      enabledFunctions: data.enabledFunctions,
      noInnioContext: data.noInnioContext,
      attachments: data.attachments,
      ragSources: data.ragSources,
      promptExamples: data.promptExamples ?? [],
    }).catch((error) => {
      showErrorMessage({
        message: t(messages.error),
      });
      throw new Error(error);
    });
  };

  const creator = mode?.creator;

  return (
    <Dialog open={open} fullWidth fullScreen={isSmall} maxWidth="md">
      <DialogTitle onClose={onClose}>
        <div className="flex flex-col">
          <div>{t(messages.title)}</div>
          {creator && (
            <div>
              <Typography variant="body2">
                {t(commonMessages.createdBy, {
                  name: `${creator.full_name} (${creator.email})`,
                })}
              </Typography>
            </div>
          )}
        </div>
      </DialogTitle>
      <DialogContent>
        <FilesDropzone {...dropzoneProps}>
          <form
            id={FORM_ID}
            onSubmit={handleSubmit(_onSubmit)}
            className="flex flex-col"
          >
            <div className="">
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => (
                  <>
                    <Typography variant="h6" id="input-slider" gutterBottom>
                      {t(commonMessages.name)}
                    </Typography>
                    <TextField
                      {...field}
                      disabled={readOnly || mode?.read_only || field.disabled}
                      label={t(commonMessages.name)}
                      value={field.value ?? ""}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </div>
            <div className="mt-8">
              <Typography variant="h6" id="input-slider" gutterBottom>
                Conversation Style
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={enableThinking}
                      onChange={(event) => {
                        setValue("enableThinking", event.target.checked);
                        if (event.target.checked) {
                          // When enabling thinking, set to default reasoning effort if it was previously 'none'
                          if (reasoningEffort === "none") {
                            setValue("reasoningEffort", defaultReasoningEffort);
                          }
                        } else {
                          // When disabling thinking, reset to 'none' to match backend ReasoningEffort enum
                          setValue("reasoningEffort", "none");
                        }
                      }}
                      disabled={readOnly || mode?.read_only}
                    />
                  }
                  label={t(messages.enableThinking)}
                />
              </FormGroup>
              {!enableThinking && (
                <div className="mt-4">
                  <TemperatureSlider
                    disabled={readOnly || mode?.read_only}
                    temperature={temperature}
                    onChange={(temperature) => {
                      setValue("temperature", temperature);
                    }}
                  />
                </div>
              )}
            </div>
            {enableThinking && (
              <div className="mt-8">
                <ReasoningEffortSelector
                  disabled={readOnly || mode?.read_only}
                  reasoningEffort={reasoningEffort}
                  onChange={(reasoningEffort) => {
                    setValue("reasoningEffort", reasoningEffort);
                  }}
                />
              </div>
            )}
            <div className="mt-8">
              <Typography variant="h6" id="input-slider" gutterBottom>
                {t(messages.systemPromptAddition)}
              </Typography>
              <Controller
                control={control}
                name="systemPromptAddition"
                render={({ field, fieldState }) => (
                  <>
                    <TextField
                      {...field}
                      multiline
                      minRows={1}
                      maxRows={10}
                      disabled={readOnly || mode?.read_only || field.disabled}
                      label={t(messages.systemPromptAddition)}
                      value={field.value ?? ""}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  </>
                )}
              />
            </div>
            <div className="mt-8">
              <DocumentTypesHeading />
              <Controller
                control={control}
                name="noInnioContext"
                render={({ field }) => (
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          disabled={
                            readOnly || mode?.read_only || field.disabled
                          }
                        />
                      }
                      label={t(messages.noInnioContext)}
                    />
                  </FormGroup>
                )}
              />
            </div>
            <div className="mt-2">
              <DocumentTypesAutocomplete
                disabled={readOnly || mode?.read_only || noInnioContext}
                documents={documents}
                onChange={(documents) => {
                  const types = documents
                    .filter(({ selected }) => selected)
                    .map(({ id }) => id);
                  setValue("documentTypes", types);
                }}
              />
            </div>
            <div className="mt-2">
              <FunctionsAutocomplete
                disabled={readOnly || mode?.read_only}
                functions={functions}
                onChange={(functions) => {
                  const types = functions
                    .filter(({ selected }) => selected)
                    .map(({ id }) => id);
                  setValue("enabledFunctions", types);
                }}
              />
            </div>
            <div className="mt-8">
              <FilesHeading />
              <div className="flex items-center">
                <FilesPreview
                  {...previewProps}
                  deleteFile={(file) => {
                    setValue(
                      "attachments",
                      attachments.filter((f) => f.name !== file.name)
                    );
                    previewProps.deleteFile(file);
                  }}
                  disabled={readOnly || mode?.read_only}
                />
                <div className="ml-4">
                  <FileUploadButton
                    {...buttonProps}
                    disabled={readOnly || mode?.read_only}
                  />
                </div>
              </div>
            </div>
            <div className="mt-8">
              <RagSourcesHeading
                read_only={readOnly || mode?.read_only}
                onRagSourceAdd={() => {
                  const source: RagSource = {
                    sharepoint_url: "",
                    status: null,
                    include_subfolders: true,
                  };
                  ragSourcesFieldArray.append(source);
                }}
              />
              {ragSourcesFieldArray.fields.map((field, index) => (
                <Fragment key={field.id}>
                  <div className="flex flex-col">
                    <div className="flex items-start">
                      <div className="flex-grow">
                        <Controller
                          control={control}
                          name={`ragSources.${index}`}
                          render={({ field, fieldState }) => (
                            <RagSourceInput
                              {...field}
                              disabled={readOnly || mode?.read_only}
                              modeId={mode?._id}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              ragSource={field.value}
                              value={field.value?.sharepoint_url ?? ""}
                              onChange={(e) => {
                                field.onChange({
                                  ...field.value,
                                  sharepoint_url: e.target.value,
                                });
                              }}
                              validationChanged={({ success, error }) => {
                                if (success) {
                                  clearErrors(`ragSources.${index}`);
                                  return;
                                }
                                setError(`ragSources.${index}`, {
                                  type: "manual",
                                  message: error,
                                });
                              }}
                            />
                          )}
                        />
                      </div>

                      <IconButton
                        className="ml-2"
                        onClick={() => ragSourcesFieldArray.remove(index)}
                        disabled={
                          readOnly ||
                          mode?.read_only ||
                          ragSources[index]?.validationResult?.loading
                        }
                        size="large"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                    <div className="flex items-center">
                      <Controller
                        control={control}
                        name={`ragSources.${index}.include_subfolders`}
                        render={({ field }) => (
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value ?? false}
                                  disabled={readOnly || mode?.read_only}
                                />
                              }
                              label={t(messages.ragSourcesSubfolder)}
                            />
                          </FormGroup>
                        )}
                      />
                    </div>
                    <Typography variant="body2" className="mt-2 text-gray-500">
                      {t(messages.ragSourcesIngestionNote)}
                    </Typography>
                  </div>
                  {ragSourcesFieldArray.fields.length > 1 && (
                    <Divider className="mt-4 mb-8" />
                  )}
                </Fragment>
              ))}
            </div>
            <div className="mt-8">
              <div className="flex items-center">
                <Typography variant="h6">
                  {t(messages.promptExamples)}
                </Typography>
                &nbsp;
                <Tooltip title={t(messages.promptExampleTooltip)}>
                  <HelpOutlineIcon fontSize="small" />
                </Tooltip>
                <IconButton
                  className="ml-2"
                  onClick={() => promptExamplesFieldArray.append("")}
                  disabled={readOnly || mode?.read_only}
                  size="large"
                >
                  <AddIcon />
                </IconButton>
              </div>
              {promptExamplesFieldArray.fields.map((field, index) => (
                <div key={field.id} className="flex items-center mb-2">
                  <Controller
                    control={control}
                    name={`promptExamples.${index}`}
                    render={({ field, fieldState }) => (
                      <>
                        <TextField
                          {...field}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          disabled={
                            readOnly || mode?.read_only || field.disabled
                          }
                          fullWidth
                          placeholder={t({
                            id: "modeUpdateDialog.promptExamplePlaceholder",
                            defaultMessage: "Enter a prompt example",
                          })}
                        />
                        <IconButton
                          className="ml-2"
                          onClick={() => promptExamplesFieldArray.remove(index)}
                          disabled={readOnly || mode?.read_only}
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  />
                </div>
              ))}
            </div>
          </form>
        </FilesDropzone>
      </DialogContent>
      <DialogActions className="flex justify-between">
        <DialogButton
          type="submit"
          variant="destructive"
          disabled={readOnly || !mode?._id}
          onClick={() => {
            if (mode?._id) onDelete?.(mode._id);
          }}
        >
          {t(commonMessages.delete)}
        </DialogButton>
        <div className="flex gap-2">
          {typeof onPublish === "function" && (
            <LoadingButton
              variant="outlined"
              loadingPosition="start"
              startIcon={<UploadIcon />}
              onClick={() => {
                if (!mode) {
                  return;
                }
                onPublish?.(mode);
              }}
            >
              Publish
            </LoadingButton>
          )}
          <LoadingButton
            form={FORM_ID}
            type="submit"
            variant="contained"
            loading={isSubmitting}
            loadingPosition="start"
            startIcon={<SaveIcon />}
            disabled={
              readOnly || mode?.read_only || isSubmitting || isSubmitSuccessful
            }
          >
            {t(commonMessages.save)}
          </LoadingButton>
        </div>
      </DialogActions>
    </Dialog>
  );
}
